import { useState } from 'react';
import { saveLead, markAsInteressado } from '@/services/leads-site';

interface LeadCaptureFormProps {
  variant?: 'default' | 'dark';
  onSuccess?: (email: string, nome: string, empresa: string) => void;
}

export default function LeadCaptureForm({ variant = 'dark', onSuccess }: LeadCaptureFormProps) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [segmento, setSegmento] = useState('');
  const [lgpdAceito, setLgpdAceito] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    setEnviando(true);
    try {
      const result = await saveLead({
        nome,
        email,
        whatsapp: whatsapp.replace(/\D/g, ''),
        empresa,
        segmento,
        maior_desafio: '',
        diagnostico: null,
        lgpd_aceito: lgpdAceito,
      });

      if (result.success) {
        await markAsInteressado(email);
        setSucesso(true);
        onSuccess?.(email, nome, empresa);
      } else {
        setErro('Erro ao salvar. Tente novamente.');
      }
    } catch {
      setErro('Erro de conexão. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  if (sucesso) {
    return (
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6 text-center">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-emerald-500/20 mx-auto mb-3">
          <i className="ri-check-double-line text-emerald-400 text-2xl" />
        </div>
        <h3 className="text-white font-black text-lg mb-1">Cadastro realizado!</h3>
        <p className="text-white/50 text-sm mb-4">Nossa equipe entrará em contato em breve.</p>
        <div className="bg-gray-900/60 border border-white/10 rounded-xl p-4 text-left space-y-2">
          <div className="flex items-center gap-2">
            <i className="ri-user-line text-emerald-400 text-sm" />
            <span className="text-white/70 text-xs">{nome}</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-building-line text-emerald-400 text-sm" />
            <span className="text-white/70 text-xs">{empresa}</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-mail-line text-emerald-400 text-sm" />
            <span className="text-white/70 text-xs">{email}</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-whatsapp-line text-emerald-400 text-sm" />
            <span className="text-white/70 text-xs">{whatsapp}</span>
          </div>
        </div>
      </div>
    );
  }

  const inputBase = variant === 'dark'
    ? 'w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/25 focus:outline-none focus:border-amber-500/50 transition-colors'
    : 'w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-amber-500/50 transition-colors';

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <input type="text" name="nome" required placeholder="Seu nome" value={nome} onChange={e => setNome(e.target.value)} className={inputBase} />
        </div>
        <div>
          <input type="email" name="email" required placeholder="Seu e-mail" value={email} onChange={e => setEmail(e.target.value)} className={inputBase} />
        </div>
        <div>
          <input type="tel" name="whatsapp" required placeholder="WhatsApp" pattern="[0-9]{10,11}" maxLength={11} value={whatsapp} onInput={e => { (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.replace(/\D/g, ''); setWhatsapp((e.target as HTMLInputElement).value); }} className={inputBase} />
        </div>
        <div>
          <input type="text" name="empresa" required placeholder="Nome da empresa" value={empresa} onChange={e => setEmpresa(e.target.value)} className={inputBase} />
        </div>
      </div>
      <div>
        <select name="segmento" required value={segmento} onChange={e => setSegmento(e.target.value)} className={`${inputBase} [&>option]:text-gray-900`}>
          <option value="">Segmento da empresa</option>
          <option value="servicos">Serviços</option>
          <option value="comercio_varejo">Comércio / Varejo</option>
          <option value="industria">Indústria</option>
          <option value="tecnologia">Tecnologia</option>
          <option value="construcao">Construção Civil</option>
          <option value="alimentacao">Alimentação</option>
          <option value="saude">Saúde</option>
          <option value="educacao">Educação</option>
          <option value="outro">Outro</option>
        </select>
      </div>
      <div className="flex items-start gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5">
        <input type="checkbox" name="lgpd_aceito" id={`lgpd_aceito_${variant}`} value="1" required checked={lgpdAceito} onChange={e => setLgpdAceito(e.target.checked)} className="mt-0.5 accent-amber-500 cursor-pointer flex-shrink-0" />
        <label htmlFor={`lgpd_aceito_${variant}`} className="text-white/40 text-xs leading-relaxed cursor-pointer text-left">
          Li e aceito a <a href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer" className="text-amber-400 underline">Política de Privacidade</a> e autorizo contato.
        </label>
      </div>
      {erro && <p className="text-red-400 text-xs text-center">{erro}</p>}
      <button type="submit" disabled={enviando} className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-gray-950 font-black text-sm py-3.5 rounded-xl transition-all duration-300 cursor-pointer">
        {enviando ? (
          <><i className="ri-loader-4-line animate-spin text-base" /><span>Enviando...</span></>
        ) : (
          <><i className="ri-send-plane-line text-base" /><span>QUERO SABER MAIS</span></>
        )}
      </button>
      <p className="text-white/25 text-xs text-center">Sem compromisso · Resposta em até 24h</p>
    </form>
  );
}